/**
 * The IRS Exempt Organizations Business Master File writes an organization's
 * status as two-digit codes. The graph keeps the codes as facts; a page shows
 * what they mean. The tables below follow the IRS's own documentation of the
 * extract (the "EO BMF" information sheet); a code the tables do not list is
 * shown as "code NN" rather than guessed, because a wrong word about an
 * organization's legal status is worse than a number.
 */
import type { OrganizationItem } from '../../src/serve/items.js';
import { formatPeriod } from './format.js';

const SUBSECTIONS: Readonly<Record<string, string>> = {
  '01': '501(c)(1) · corporations organized under an act of Congress',
  '02': '501(c)(2) · title-holding corporations',
  '03': '501(c)(3) · charitable, educational, religious, scientific, or literary',
  '04': '501(c)(4) · social welfare organizations',
  '05': '501(c)(5) · labor, agricultural, and horticultural organizations',
  '06': '501(c)(6) · business leagues and chambers of commerce',
  '07': '501(c)(7) · social and recreational clubs',
  '08': '501(c)(8) · fraternal beneficiary societies',
  '09': '501(c)(9) · voluntary employee beneficiary associations',
  '10': '501(c)(10) · domestic fraternal societies',
  '11': '501(c)(11) · teachers’ retirement fund associations',
  '12': '501(c)(12) · benevolent life insurance associations and mutual utilities',
  '13': '501(c)(13) · cemetery companies',
  '14': '501(c)(14) · state-chartered credit unions',
  '15': '501(c)(15) · mutual insurance companies',
  '16': '501(c)(16) · cooperative crop-financing organizations',
  '17': '501(c)(17) · supplemental unemployment benefit trusts',
  '18': '501(c)(18) · employee-funded pension trusts',
  '19': '501(c)(19) · veterans’ organizations',
  '21': '501(c)(21) · black lung benefit trusts',
  '22': '501(c)(22) · withdrawal liability payment funds',
  '23': '501(c)(23) · veterans’ organizations founded before 1880',
  '25': '501(c)(25) · title-holding corporations for multiple parents',
  '26': '501(c)(26) · state-sponsored high-risk health coverage organizations',
  '27': '501(c)(27) · state-sponsored workers’ compensation organizations',
  '29': '501(c)(29) · qualified nonprofit health insurance issuers',
  '40': '501(d) · religious and apostolic associations',
  '50': '501(e) · cooperative hospital service organizations',
  '60': '501(f) · cooperative service organizations of educational institutions',
  '70': '501(k) · child care organizations',
  '71': '501(n) · charitable risk pools',
  '81': '529 · qualified state-sponsored tuition programs',
  '82': '527 · political organizations',
  '90': '4947(a)(2) · split-interest trust',
  '91': '4947(a)(1) · non-exempt charitable trust',
  '92': '4947(a)(1) · non-exempt private foundation',
};

const FOUNDATIONS: Readonly<Record<string, string>> = {
  '00': 'Not a 501(c)(3) organization',
  '02': 'Private operating foundation exempt from excise tax on investment income',
  '03': 'Private operating foundation',
  '04': 'Private non-operating foundation',
  '09': 'Suspense',
  '10': 'Public charity · church (170(b)(1)(A)(i))',
  '11': 'Public charity · school (170(b)(1)(A)(ii))',
  '12': 'Public charity · hospital or medical research organization (170(b)(1)(A)(iii))',
  '13': 'Public charity · supports a governmental college or university (170(b)(1)(A)(iv))',
  '14': 'Public charity · governmental unit (170(b)(1)(A)(v))',
  '15': 'Public charity · publicly supported (170(b)(1)(A)(vi))',
  '16': 'Public charity · supported by program revenue and contributions (509(a)(2))',
  '17': 'Public charity · supporting organization (509(a)(3))',
  '18': 'Public charity · tests for public safety (509(a)(4))',
  '21': 'Supporting organization, type I (509(a)(3))',
  '22': 'Supporting organization, type II (509(a)(3))',
  '23': 'Supporting organization, type III, functionally integrated (509(a)(3))',
  '24': 'Supporting organization, type III, not functionally integrated (509(a)(3))',
};

const PRIVATE_FOUNDATION_CODES = new Set(['02', '03', '04']);

const STATUSES: Readonly<Record<string, string>> = {
  '01': 'Unconditional exemption',
  '02': 'Conditional exemption',
  '12': 'Trust described in section 4947(a)(2)',
  '25': 'Terminating private foundation status',
};

const describe = (table: Readonly<Record<string, string>>, code: string | null): string | null => {
  if (code === null || code.length === 0) {
    return null;
  }
  return table[code] ?? `code ${code}`;
};

const RETURN_TYPES: Readonly<Record<string, string>> = { '990': '990', '990EZ': '990-EZ', '990PF': '990-PF', '990T': '990-T', '990N': '990-N' };
/** A return type as the IRS writes it on the form; a code the table does not know is shown as written. */
export const describeReturnType = (code: string | null): string | null => (code === null ? null : (RETURN_TYPES[code] ?? code));

export const describeSubsection = (code: string | null): string | null => describe(SUBSECTIONS, code);
export const describeFoundation = (code: string | null): string | null => describe(FOUNDATIONS, code);
export const describeStatus = (code: string | null): string | null => describe(STATUSES, code);
export const isPrivateFoundation = (foundationCode: string | null): boolean => foundationCode !== null && PRIVATE_FOUNDATION_CODES.has(foundationCode);

/** The ruling date is written YYYYMM; the master file writes 000000 when there is none. */
export const describeRuling = (yyyymm: string | null): string | null => {
  if (yyyymm === null || !/^\d{6}$/.test(yyyymm) || yyyymm === '000000' || Number(yyyymm.slice(4)) < 1 || Number(yyyymm.slice(4)) > 12) {
    return null;
  }
  return formatPeriod(yyyymm);
};

export interface MasterFileDescription {
  readonly exemption: string | null;
  readonly foundation: string | null;
  readonly status: string | null;
  readonly ruling: string | null;
}

export const describeMasterFile = (masterFile: OrganizationItem['masterFile']): MasterFileDescription =>
  masterFile === null
    ? { exemption: null, foundation: null, status: null, ruling: null }
    : {
        exemption: describeSubsection(masterFile.subsection),
        foundation: describeFoundation(masterFile.foundationCode),
        status: describeStatus(masterFile.status),
        ruling: describeRuling(masterFile.rulingDate),
      };
